/**
 * Legacy URL → νέο URL redirects για τη μετάβαση από το παλιό PrestaShop
 * (lianiki.mrpanda.gr) στο νέο eshop. Καθαρές συναρτήσεις χωρίς πρόσβαση στη
 * βάση, ώστε να τρέχουν με ασφάλεια και σε SSR και σε client navigation.
 *
 * Σειρά ελέγχου:
 *  1. Ρητός χάρτης `LEGACY_REDIRECTS` (γέμισέ τον από το CSV old→new).
 *  2. PrestaShop non-friendly URLs με query (?id_product / ?id_category / ...).
 *  3. PrestaShop friendly product URLs που τελειώνουν σε `.html`.
 *  4. PrestaShop friendly category URLs `/{id}-{slug}` (χωρίς .html).
 *
 * Οι σελίδες προορισμού (/p, /c, /brand) εντοπίζουν το προϊόν/κατηγορία από το
 * αριθμητικό id, οπότε το κείμενο του slug δεν χρειάζεται να είναι ακριβές.
 */

/**
 * Ρητές αντιστοιχίσεις old path → new path από το CSV σου.
 * Παράδειγμα:
 *   "/palia-selida": "/offers",
 *   "/15-prosfores": "/offers",
 */
export const LEGACY_REDIRECTS: Record<string, string> = {
  // TODO: συμπλήρωσε από το CSV (old_path → new_path)
  "/9inqsamntf8sc6vj": "/",
};

function leadingId(seg: string): string | null {
  const m = /^(\d+)(?:-|$)/.exec(seg);
  return m ? m[1] : null;
}

/**
 * Εξάγει το αριθμητικό PrestaShop id από ένα segment, είτε βρίσκεται στην
 * αρχή (`{id}-{slug}`) είτε στο τέλος (`{slug}-{id}`).
 */
function extractId(seg: string): string | null {
  const lead = leadingId(seg);
  if (lead) return lead;
  const tail = /-(\d+)$/.exec(seg);
  return tail ? tail[1] : null;
}

function normalizePath(path: string): string {
  // αφαίρεση query/hash και διπλών/τελικών slashes
  let p = path.split("?")[0].split("#")[0];
  p = p.replace(/\/{2,}/g, "/");
  if (p.length > 1) p = p.replace(/\/+$/, "");
  return p || "/";
}

/**
 * Επιστρέφει το νέο path για ένα παλιό PrestaShop URL, ή `null` αν δεν
 * αναγνωρίζεται (οπότε ο caller πρέπει να γυρίσει 404).
 */
export function resolveLegacyRedirect(rawPath: string, searchStr = ""): string | null {
  const path = normalizePath(rawPath);

  // 1) Ρητός χάρτης (με και χωρίς trailing slash)
  if (LEGACY_REDIRECTS[path]) return LEGACY_REDIRECTS[path];
  if (LEGACY_REDIRECTS[rawPath]) return LEGACY_REDIRECTS[rawPath];

  // 2) PrestaShop non-friendly URLs (query string)
  const params = new URLSearchParams(searchStr.startsWith("?") ? searchStr.slice(1) : searchStr);
  const idProduct = params.get("id_product");
  if (idProduct && /^\d+$/.test(idProduct)) return `/p/${idProduct}`;
  const idManufacturer = params.get("id_manufacturer");
  if (idManufacturer && /^\d+$/.test(idManufacturer)) return `/brand/${idManufacturer}`;
  const idCategory = params.get("id_category");
  if (idCategory && /^\d+$/.test(idCategory)) return `/c/${idCategory}`;

  // Απομόνωση των path segments, αγνοώντας τυχόν language prefix (/el/, /en/).
  const segs = path.split("/").filter(Boolean);
  if (segs.length && /^[a-z]{2}$/.test(segs[0])) segs.shift();
  if (segs.length === 0) return null;

  // 2a) Παλιές σελίδες σύνδεσης PrestaShop: `/σύνδεση?back=<full product url>`
  // (χιλιάδες τέτοια indexed στο Google). Ανακατευθύνουμε στο προϊόν του
  // `back` ώστε να περνά η αξία SEO στη σωστή σελίδα αντί για 404. Αν δεν
  // υπάρχει αναγνωρίσιμο `back`, πάμε στο νέο login.
  const noLang = "/" + segs.join("/");
  if (noLang === "/σύνδεση" || noLang === "/login") {
    const back = params.get("back");
    if (back) {
      // Το `back` είναι πλήρες URL (ή path). Κράτα μόνο το pathname/query.
      let backPath = back;
      let backSearch = "";
      try {
        const u = new URL(back, "https://lianiki.mrpanda.gr");
        backPath = u.pathname;
        backSearch = u.search;
      } catch {
        const qi = back.indexOf("?");
        if (qi >= 0) {
          backPath = back.slice(0, qi);
          backSearch = back.slice(qi);
        }
      }
      const dest = resolveLegacyRedirect(decodeURIComponent(backPath), backSearch);
      return dest ?? "/";
    }
    return null;
  }

  // 2a') Παλιές σελίδες αναζήτησης PrestaShop: `/search?controller=search&s=<όρος>`
  if (segs[0] === "search") {
    const term = params.get("s") ?? params.get("search_query") ?? "";
    if (term && !/[{}]/.test(term)) return `/search?q=${encodeURIComponent(term)}`;
    return "/search";
  }

  // 2b) Ρητά προθέματα PrestaShop: `/category/{id}-{slug}`, `/brand/{id}-{slug}`.
  if (segs.length >= 2 && (segs[0] === "category" || segs[0] === "c")) {
    const id = extractId(segs[1]);
    if (id) return `/c/${id}`;
  }
  if (segs.length >= 2 && segs[0] === "brand") {
    const id = extractId(segs[1]);
    if (id) return `/brand/${id}`;
  }

  // 2c) Παλιές σελίδες ετικετών `/tags/{όρος}` → αναζήτηση.
  if (segs.length >= 2 && segs[0] === "tags") {
    const term = decodeURIComponent(segs[1]);
    if (term) return `/search?q=${encodeURIComponent(term)}`;
  }

  const last = segs[segs.length - 1];

  // 3) Friendly product URL: τελειώνει σε .html με αριθμητικό id μπροστά
  if (last.toLowerCase().endsWith(".html")) {
    const base = last.slice(0, -5);
    const id = leadingId(base);
    if (id) return `/p/${base}`;
    return null;
  }

  // 4) Friendly category URL: μοναδικό segment της μορφής {id}-{slug}
  if (segs.length === 1) {
    // id στην αρχή → κράτα το πλήρες slug (η σελίδα διαβάζει το leading id)
    if (leadingId(last)) return `/c/${last}`;
    // id στο τέλος (`{slug}-{id}`) → ανακατεύθυνε στο σκέτο id
    const tail = /-(\d+)$/.exec(last);
    if (tail) return `/c/${tail[1]}`;
  }

  return null;
}